import React, { useEffect, useRef, useState } from "react"
import type { IconType } from "react-icons"
import { LuCircleCheck, LuCircleX, LuCopy, LuDownload, LuEllipsis, LuMail, LuMessageCircle } from "react-icons/lu"
import styles from "asset/styles/quotation/quotationPreviewV2.module.scss"

type IMoreActionItem = {
	key: string
	label: string
	Icon: IconType
	iconClassName?: string
	onClick?: () => void
	flagDisabled?: boolean
	flagComingSoon?: boolean
	flagVisible: boolean
}

type IQuotationPreviewV2MoreActionsProps = {
	flagDownloadDisabled: boolean
	flagShowMarkConfirmedLost: boolean
	flagBusy: boolean
	onDuplicate: () => void
	onDownload: () => void
	onMarkConfirmed: () => void
	onMarkLost: () => void
}

const QuotationPreviewV2MoreActions: React.FC<IQuotationPreviewV2MoreActionsProps> = (props) => {

	const {
		flagDownloadDisabled,
		flagShowMarkConfirmedLost,
		flagBusy,
		onDuplicate,
		onDownload,
		onMarkConfirmed,
		onMarkLost,
	} = props

	const [isOpen, setIsOpen] = useState(false)
	const wrapperRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (!isOpen) return
		const handleOutsideClick = (event: MouseEvent) => {
			if (!wrapperRef.current?.contains(event.target as Node)) setIsOpen(false)
		}
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") setIsOpen(false)
		}
		document.addEventListener("mousedown", handleOutsideClick)
		document.addEventListener("keydown", handleKeyDown)
		return () => {
			document.removeEventListener("mousedown", handleOutsideClick)
			document.removeEventListener("keydown", handleKeyDown)
		}
	}, [isOpen])

	const items: IMoreActionItem[] = [
		{ key: "duplicate", label: "Duplicate", Icon: LuCopy, onClick: onDuplicate, flagVisible: true },
		{ key: "download", label: "Download", Icon: LuDownload, onClick: onDownload, flagDisabled: flagDownloadDisabled, flagVisible: true },
		{
			key: "confirmed",
			label: "Mark Confirmed",
			Icon: LuCircleCheck,
			iconClassName: styles.menuIconSuccess,
			onClick: onMarkConfirmed,
			flagDisabled: flagBusy,
			flagVisible: flagShowMarkConfirmedLost,
		},
		{
			key: "lost",
			label: "Mark Lost",
			Icon: LuCircleX,
			iconClassName: styles.menuIconDanger,
			onClick: onMarkLost,
			flagDisabled: flagBusy,
			flagVisible: flagShowMarkConfirmedLost,
		},
		{ key: "email", label: "Send via Email", Icon: LuMail, flagComingSoon: true, flagVisible: true },
		{ key: "whatsapp", label: "Send via WhatsApp", Icon: LuMessageCircle, flagComingSoon: true, flagVisible: true },
	]

	const handleItemClick = (item: IMoreActionItem) => {
		if (item.flagDisabled || item.flagComingSoon) return
		setIsOpen(false)
		item.onClick?.()
	}

	return (
		<div className={styles.moreActions} ref={wrapperRef}>
			<button
				type="button"
				className={styles.outlineButton}
				onClick={() => setIsOpen((prev) => !prev)}
				aria-haspopup="menu"
				aria-expanded={isOpen}
			>
				<LuEllipsis size={16} />
				More actions
			</button>
			{isOpen && (
				<div className={styles.moreActionsMenu} role="menu">
					{items.filter((item) => item.flagVisible).map((item) => (
						<button
							key={item.key}
							type="button"
							role="menuitem"
							className={styles.moreActionsItem}
							onClick={() => handleItemClick(item)}
							disabled={item.flagDisabled || item.flagComingSoon}
						>
							<item.Icon size={16} className={item.iconClassName} />
							<span>{item.label}</span>
							{item.flagComingSoon && <span className={styles.comingSoonBadge}>Coming soon</span>}
						</button>
					))}
				</div>
			)}
		</div>
	)
}

export default QuotationPreviewV2MoreActions
